import { some } from 'lodash';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

/**
* @module Spotlight
* @description A React component that creates a highlighted focus area around 
* specified elements during tutorials, with smooth transitions and responsive positioning.
* 
* @component
* @description
* Creates visual focus by:
* 1. Spotlight Positioning:
*    - Tracks single or multiple target elements
*    - Calculates containing rectangle
*    - Handles dynamic target changes
*    - Maintains responsive positioning
* 
* 2. Animation Management:
*    - Smooth transitions between targets
*    - Handles element mutations
*    - Controls movement states
*    - Manages animation timing
* 
* 3. DOM Observation:
*    - Watches for attribute changes
*    - Tracks class/style mutations
*    - Updates position after animations
*    - Cleans up observers
* 
* @example
* // Single target
* // Redux state:
* {
*   ui: {
*     spotlightTarget: 'menuItem1'
*   }
* }
* 
* // Multiple targets
* {
*   ui: {
*     spotlightTarget: ['menuItem1', 'menuItem2']
*   }
* }
* 
* @note
* Visual Properties:
* - Semi-transparent overlay (0.6 opacity)
* - 8px padding around targets
* - 5px border radius
* - White inner shadow
* - Gray outer overlay
* 
* Performance Considerations:
* - Uses MutationObserver for DOM changes
* - Debounces position updates
* - Cleans up timers and observers
* - Optimizes animation frames
* 
* State Management:
* - Tracks movement state
* - Maintains previous target
* - Handles tutorial modal state
* - Controls visibility
* 
* Dependencies:
* - Uses Redux for state
* - Requires DOM mutation support
* - Needs CSS transition support
* - Relies on tutorial modal state
*/
const Spotlight = (props) => {
  const [spotlightStyles, setSpotlightStyles] = useState({});
  const [isMoving, setIsMoving] = useState(false);
  const [prevTarget, setPrevTarget] = useState(null);
  const updateTimeout = useRef(null);
  const target = useSelector(state => state.ui.spotlightTarget);
  const isTutorialOpen = useSelector(state => state.ui.isModalOpen && some(state.ui.modals, modal => modal.modalName === 'tutorial'));

  const updateSpotlightPosition = () => {
    // Handle single string or array of target IDs
    const targetIds = Array.isArray(target) ? target : [target];
    
    // Get all valid target elements
    const targetElements = targetIds
      .map(id => document.getElementById(id))
      .filter(Boolean);

    if (targetElements.length === 0) return;

    // Get DOMRect for each element
    const rects = targetElements.map(element => element.getBoundingClientRect());

    // Calculate the containing rectangle
    const containingRect = rects.reduce((container, rect) => ({
      left: Math.min(container.left, rect.left),
      top: Math.min(container.top, rect.top),
      right: Math.max(container.right, rect.right),
      bottom: Math.max(container.bottom, rect.bottom)
    }), {
      left: Infinity,
      top: Infinity,
      right: -Infinity,
      bottom: -Infinity
    });

    // Convert to dimensions
    const dimensions = {
      width: containingRect.right - containingRect.left,
      height: containingRect.bottom - containingRect.top,
      left: containingRect.left,
      top: containingRect.top
    };
    
    setSpotlightStyles({
      width: `${dimensions.width + 8}px`,
      height: `${dimensions.height + 8}px`,
      transform: `translate(${dimensions.left - 4}px, ${dimensions.top - 4}px)`,
      position: 'absolute',
      top: 0,
      left: 0,
      boxShadow: 'inset #fff 0 0 0 0px, var(--app-medium-gray) 0 0 0 2000px',
      borderRadius: '5px',
      opacity: 0.6
    });
  };

  useEffect(() => {
    if (!target || !isTutorialOpen) return;

    // Track target changes to trigger movement animation
    if (prevTarget !== target) {
      setIsMoving(true);
      setPrevTarget(target);
    }

    // Clear any existing update timeout
    if (updateTimeout.current) {
      clearTimeout(updateTimeout.current);
    }

    // Initial position update
    updateSpotlightPosition();

    // Set up mutation observer to watch for menu changes
    const targetElement = document.getElementById(target);
    if (!targetElement) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
          // Update after a short delay to allow animations to complete
          updateTimeout.current = setTimeout(updateSpotlightPosition, 300);
        }
      });
    });

    observer.observe(targetElement, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      subtree: true
    });

    // Handle movement animation
    if (isMoving) {
      const timer = setTimeout(() => setIsMoving(false), 300);
      return () => {
        clearTimeout(timer);
        observer.disconnect();
        if (updateTimeout.current) {
          clearTimeout(updateTimeout.current);
        }
      };
    }

    return () => {
      observer.disconnect();
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }
    };
  }, [target, isTutorialOpen, prevTarget, isMoving]);

  return (
    <Fragment>
      {target && <div 
        className={`spotlight ${target ? 'is-visible' : ''} ${isMoving ? 'is-moving' : ''}`} 
        style={spotlightStyles}
      />}
    </Fragment>  
  );
};

export default Spotlight;
